
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Noticias() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('recentes');

  useEffect(() => {
    // Update page title and meta description
    document.title = `${t('news.hero.title')} - Market News and Analysis | Lions Trade Expert`;
    
    // Add JSON-LD structured data
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      "headline": t('news.hero.title'),
      "description": t('news.hero.subtitle'),
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/noticias`,
      "author": {
        "@type": "Organization",
        "name": "Lions Trade Expert"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Lions Trade Expert",
        "logo": {
          "@type": "ImageObject",
          "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/logo.png`
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/noticias`
      },
      "about": {
        "@type": "Thing",
        "name": "Commodity Market News",
        "description": "Latest news and analysis about commodity markets, sugar and coffee prices, export trends"
      },
      "keywords": "commodity news, market analysis, sugar prices, coffee prices, export trends, agribusiness news",
      "inLanguage": "en",
      "datePublished": "2024-01-15",
      "dateModified": "2024-01-15"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [t]);

  const recentNews = [
    {
      id: 1,
      title: 'Safra de café 2024 supera expectativas no Sul de Minas',
      date: '15 de Janeiro, 2024',
      category: 'Café',
      summary: 'Produtores da região relatam aumento de 12% na produtividade comparado ao ano anterior...',
      image: 'coffee-harvest'
    },
    {
      id: 2,
      title: 'Exportações de açúcar batem recorde no primeiro trimestre',
      date: '12 de Janeiro, 2024',
      category: 'Açúcar',
      summary: 'Brasil exportou 8,2 milhões de toneladas de açúcar nos primeiros três meses do ano...',
      image: 'sugar-export'
    },
    {
      id: 3,
      title: 'Novo terminal portuário amplia capacidade em Santos',
      date: '10 de Janeiro, 2024',
      category: 'Logística',
      summary: 'Investimento de R$ 2 bilhões aumenta capacidade de movimentação de commodities...',
      image: 'port-terminal'
    },
    {
      id: 4,
      title: 'Acordo comercial facilita exportação para países árabes',
      date: '8 de Janeiro, 2024',
      category: 'Comercial',
      summary: 'Novo tratado reduz tarifas e simplifica procedimentos para exportação...',
      image: 'trade-agreement'
    }
  ];

  const marketData = [
    {
      product: 'Açúcar VHP',
      price: 'US$ 425/ton',
      change: '+2.3%',
      trend: 'up'
    },
    {
      product: 'Café Arábica',
      price: 'US$ 1.85/lb',
      change: '-1.2%',
      trend: 'down'
    },
    {
      product: 'Café Robusta',
      price: 'US$ 2.150/ton',
      change: '+0.8%',
      trend: 'up'
    },
    {
      product: 'Açúcar Cristal',
      price: 'US$ 480/ton',
      change: '+1.5%',
      trend: 'up'
    }
  ];

  const charts = [
    {
      title: 'Exportação de Açúcar (Milhões de Toneladas)',
      data: 'Janeiro: 2.1M, Fevereiro: 2.4M, Março: 2.8M, Abril: 3.2M'
    },
    {
      title: 'Exportação de Café (Milhões de Sacas)',
      data: 'Janeiro: 3.5M, Fevereiro: 3.8M, Março: 4.1M, Abril: 3.9M'
    }
  ];

  const forecasts = [
    {
      title: 'Previsão de Safra 2024/25',
      content: 'Estimativa indica crescimento de 8% na produção de café arábica devido às condições climáticas favoráveis.',
      icon: 'ri-plant-line'
    },
    {
      title: 'Novos Acordos Comerciais',
      content: 'Negociações em andamento com Vietnã e Tailândia podem abrir novos mercados para açúcar brasileiro.',
      icon: 'ri-handshake-line'
    },
    {
      title: 'Impacto Climático',
      content: 'El Niño pode afetar produção na região Sudeste. Monitoramento intensificado para próximos meses.',
      icon: 'ri-cloud-windy-line'
    },
    {
      title: 'Inovações Logísticas',
      content: 'Projeto piloto de automação portuária reduz tempo de embarque em 30% no Porto de Santos.',
      icon: 'ri-robot-line'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center text-white overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.4)), url('https://public.readdy.ai/ai/img_res/d776d401b99a7b48f0f50c1d53468f62.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30"></div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <h1 className="text-6xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white to-green-200 bg-clip-text text-transparent">
              {t('news.hero.title')}
            </h1>
            <h2 className="text-4xl md:text-5xl font-light mb-8 text-green-300">
              {t('news.hero.subtitle')}
            </h2>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-white text-2xl"></i>
        </div>
      </section>

      {/* Market Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-green-600 to-green-800 bg-clip-text text-transparent">
              {t('news.market.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-green-400 to-green-600 mx-auto mb-6"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {marketData.map((item, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <h3 className="font-bold text-gray-800 mb-2">{item.product}</h3>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-gray-900">{item.price}</span>
                  <span className={`flex items-center text-sm font-medium ${
                    item.trend === 'up' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    <i className={`ri-arrow-${item.trend === 'up' ? 'up' : 'down'}-line mr-1`}></i>
                    {item.change}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tabs Navigation */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center mb-8">
            <div className="bg-gray-100 rounded-full p-1">
              <button
                onClick={() => setActiveTab('recentes')}
                className={`px-6 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'recentes'
                    ? 'bg-green-600 text-white'
                    : 'text-gray-600 hover:text-green-600'
                }`}
              >
                {t('news.tabs.recent')}
              </button>
              <button
                onClick={() => setActiveTab('analises')}
                className={`px-6 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'analises'
                    ? 'bg-green-600 text-white'
                    : 'text-gray-600 hover:text-green-600'
                }`}
              >
                {t('news.tabs.analysis')}
              </button>
              <button
                onClick={() => setActiveTab('previsoes')}
                className={`px-6 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'previsoes'
                    ? 'bg-green-600 text-white'
                    : 'text-gray-600 hover:text-green-600'
                }`}
              >
                {t('news.tabs.forecasts')}
              </button>
            </div>
          </div>

          {/* Recent News Tab */}
          {activeTab === 'recentes' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {recentNews.map((news) => (
                <article key={news.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                  <div 
                    className="h-48"
                    style={{
                      backgroundImage: `url('https://readdy.ai/api/search-image?query=Brazilian%20commodity%20news%20$%7Bnews.category.toLowerCase%28%29%7D%2C%20professional%20business%20photography%2C%20modern%20industrial%20setting%2C%20clean%20composition%2C%20high%20quality%2C%20news%20worthy%20image&width=400&height=300&seq=${news.image}&orientation=squarish')`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'object-top'
                    }}
                  ></div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded">
                        {news.category}
                      </span>
                      <span className="text-gray-500 text-sm">{news.date}</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{news.title}</h3>
                    <p className="text-gray-600 text-sm">{news.summary}</p>
                  </div>
                </article>
              ))}
            </div>
          )}

          {/* Analysis Tab */}
          {activeTab === 'analises' && (
            <div className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {charts.map((chart, index) => (
                  <div key={index} className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-bold mb-4">{chart.title}</h3>
                    <div className="bg-gray-50 rounded-lg p-4 h-64 flex items-center justify-center">
                      <div className="text-center text-gray-600">
                        <i className="ri-bar-chart-line text-4xl mb-4"></i>
                        <p className="text-sm">{chart.data}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold mb-4">{t('news.analysis.title')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium mb-2">{t('news.analysis.positive.title')}</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• {t('news.analysis.positive.climate')}</li>
                      <li>• {t('news.analysis.positive.demand')}</li>
                      <li>• {t('news.analysis.positive.infrastructure')}</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">{t('news.analysis.challenges.title')}</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• {t('news.analysis.challenges.exchange')}</li>
                      <li>• {t('news.analysis.challenges.competition')}</li>
                      <li>• {t('news.analysis.challenges.costs')}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Forecasts Tab */}
          {activeTab === 'previsoes' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {forecasts.map((forecast, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <i className={`${forecast.icon} text-blue-600 text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold mb-3">{forecast.title}</h3>
                      <p className="text-gray-600">{forecast.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-green-600 to-green-800 bg-clip-text text-transparent">
            {t('news.newsletter.title')}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-green-400 to-green-600 mx-auto mb-8"></div>
          <p className="text-xl text-gray-800 mb-8">
            {t('news.newsletter.description')}
          </p>
          <form 
            id="newsletter-form"
            data-readdy-form
            action="https://readdy.ai/api/form/d3c69afjefcg4ojr34rg"
            method="POST"
            className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto"
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.target as HTMLFormElement;
              const formData = new FormData(form);
              
              fetch('https://readdy.ai/api/form/d3c69afjefcg4ojr34rg', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams(formData as any).toString()
              }).then(() => {
                alert('Inscrição realizada com sucesso!');
                form.reset();
              }).catch(() => {
                alert('Erro ao realizar inscrição. Tente novamente.');
              });
            }}
          >
            <input
              type="email"
              name="email"
              placeholder={t('news.newsletter.placeholder')}
              required
              className="flex-1 px-4 py-3 rounded-lg text-gray-900 placeholder-gray-500"
            />
            <button
              type="submit"
              className="bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('news.newsletter.subscribe')}
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
